import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import LOGO_BASE64 from "../../assets/data/logoBase64";
import RangeFilter from "../../components/RangeFilter";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  IconButton,
  Divider,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Chip,
  Tooltip,
} from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import { ArrowBack } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { MdPhoneAndroid, MdLocationOn } from "react-icons/md";
import Stack from "@mui/material/Stack";
import { FaTrash, FaEdit, FaPrint, FaWhatsapp } from "react-icons/fa";
import TrayBalanceModal from "../../components/TrayBalanceModal";
import { useAuth } from "../../context/AuthContext";
import { useRef } from "react";
import { getTrays } from "../../actions";
import ConfirmDialog from "../../components/ConfirmDialog";
import "dayjs/locale/en-in";
import { calculatePastTransactionsBalance } from "../../utils/calculateTotals";
import { buildBalanceSheetHtml } from "../../utils/balanceSheetHtml";
import {
  buildReceiptTable,
  receiptTableCss,
  receiptSlipCss,
} from "../../utils/receiptTableHtml";

const TRANSACTION_FORM_DEFAULTS = {
  stockGiven: false,
  paymentDone: false,
  traysReturned: false,
  discountGiven: false,
  dateOfTransaction: null,
  paymentAmount: "",
  discount: "",
  sales: [
    {
      itemName: "",
      numberOfItems: undefined,
      costPerItem: undefined,
      trayTypeId: undefined,
    },
  ],
  trays: [
    {
      trayTypeId: undefined,
      numberOfTrays: undefined,
      excludePayment: false,
    },
  ],
  labourCharges: {
    enabled: false,
    numberOfItems: undefined,
    costPerItem: undefined,
  },
  damageRefund: {
    enabled: false,
  },
  damageRefunds: [
    {
      selectedSaleIndex: undefined,
      numberOfItems: undefined,
      costPerItem: undefined,
      total: undefined,
    },
  ],
};

/** Transaction Validation Schema */
const transactionSchema = Yup.object({
  stockGiven: Yup.boolean().default(false),
  paymentDone: Yup.boolean().default(false),
  traysReturned: Yup.boolean().default(false),
  discountGiven: Yup.boolean().default(false),

  dateOfTransaction: Yup.date()
    .nullable()
    .when(["stockGiven", "paymentDone", "traysReturned", "discountGiven"], {
      is: (stockGiven, paymentDone, traysReturned, discountGiven) =>
        Boolean(stockGiven) ||
        Boolean(paymentDone) ||
        Boolean(traysReturned) ||
        Boolean(discountGiven),
      then: (schema) =>
        schema
          .required("Date is required")
          .max(new Date(), "Transaction cannot be in future"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),

  sales: Yup.array().when("stockGiven", {
    is: true,
    then: () =>
      Yup.array()
        .of(
          Yup.object({
            itemName: Yup.string().required("Item required"),
            numberOfItems: Yup.number()
              .required("Quantity is required")
              .positive("Quantity must be positive"),
            costPerItem: Yup.number().required("Cost required").positive(),
            trayTypeId: Yup.number(),
          })
        )
        .min(1, "At least one sale required"),
  }),

  trays: Yup.array().when("traysReturned", {
    is: true,
    then: () =>
      Yup.array()
        .of(
          Yup.object({
            numberOfTrays: Yup.number()
              .required("Quantity is required")
              .positive("Quantity must be positive"),
            trayTypeId: Yup.number().required("Tray type is required"),
            excludePayment: Yup.boolean().default(false),
          })
        )
        .min(1, "At least one tray required"),
  }),

  labourCharges: Yup.object({
    enabled: Yup.boolean().default(false),
    numberOfItems: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        if (
          originalValue === "" ||
          originalValue === null ||
          originalValue === undefined
        ) {
          return null;
        }
        const num = Number(originalValue);
        return isNaN(num) ? null : num;
      })
      .when("enabled", {
        is: true,
        then: (schema) =>
          schema.required("Qty is required").positive("Qty must be positive"),
        otherwise: (schema) => schema.nullable(),
      }),
    costPerItem: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        if (
          originalValue === "" ||
          originalValue === null ||
          originalValue === undefined
        ) {
          return null;
        }
        const num = Number(originalValue);
        return isNaN(num) ? null : num;
      })
      .when("enabled", {
        is: true,
        then: (schema) =>
          schema.required("Cost is required").positive("Cost must be positive"),
        otherwise: (schema) => schema.nullable(),
      }),
  }),

  damageRefund: Yup.object({
    enabled: Yup.boolean().default(false),
  }),

  damageRefunds: Yup.array().when("damageRefund.enabled", {
    is: true,
    then: () =>
      Yup.array()
        .of(
          Yup.object({
            selectedSaleIndex: Yup.number().required("Select item"),
            numberOfItems: Yup.number().required("Qty required").positive(),
            costPerItem: Yup.number().required("Cost required").positive(),
          })
        )
        .min(1, "At least one refund required"),
    otherwise: () => Yup.array().nullable().notRequired(), // FIX: Form will now submit if unchecked
  }),

  discount: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return null;
      }
      const num = Number(originalValue);
      return isNaN(num) ? null : num;
    })
    .when("discountGiven", {
      is: true,
      then: (schema) =>
        schema
          .required("Discount amount is required")
          .positive("Discount amount must be positive"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),

  paymentAmount: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      )
        return null;
      const num = Number(originalValue);
      return isNaN(num) ? null : num;
    })
    .when("paymentDone", {
      is: true,
      then: (schema) =>
        schema
          .required("Amount is required")
          .positive("Amount must be positive"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
});

/**Payment Validation Schema */
const paymentSchema = Yup.object({
  amount: Yup.number()
    .typeError("Amount is required")
    .required("Amount is required")
    .positive("Amount must be greater than zero"),
  paymentDate: Yup.date()
    .required("Payment date is required")
    .max(new Date(), "Payment date cannot be in the future"),
  paymentMethod: Yup.string()
    .required("Payment method is required")
    .oneOf(["NEFT", "UPI", "Cash", "Net Banking"], "Invalid payment method"),
});

function CustomerDetails() {
  const [customerDetails, setCustomerDetails] = useState({});
  const [selectedTransactionId, setSelectedTransactionId] = useState("");
  const [open, setOpen] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transactionList, setTransactionList] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [items, setItems] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const params = useParams();
  const { id: customerId } = params;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();
  const [openTrayModal, setOpenTrayModal] = useState(false);
  const [excludePayment, setExcludePayment] = useState(false);
  const [editTransaction, setEditTransaction] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptTransaction, setReceiptTransaction] = useState(null);
  const [trayTypes, setTrayTypes] = useState([]);

  const { user } = useAuth();

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  /**Fetch Customer + Transactions */
  useEffect(() => {
    if (!customerId) return;

    setLoadingTx(true);

    const payload = { customerId };
    const hasDateFilter = Boolean(dateRange.startDate && dateRange.endDate);

    if (hasDateFilter) {
      payload.startDate = dayjs(dateRange.startDate).format("YYYY-MM-DD");
      payload.endDate = dayjs(dateRange.endDate).format("YYYY-MM-DD");
    }

    const promises = [
      axiosInstance.post("/customers/details", { customerId }),
      axiosInstance.post("/transactions/list", payload),
      axiosInstance.get("/stock"),
      getTrays(),
    ];

    if (hasDateFilter) {
      promises.push(axiosInstance.post("/transactions/list", { customerId }));
    }

    Promise.all(promises)
      .then(([custRes, txRes, stockRes, traysList, allTxRes]) => {
        setCustomerDetails(custRes?.data?.result || {});
        setTransactionList(txRes?.data?.result || []);
        setItems(stockRes?.data?.list || []);
        setTrayTypes(Array.isArray(traysList) ? traysList : []);
        setAllTransactions(
          allTxRes?.data?.result || txRes?.data?.result || []
        );
      })
      .catch((err) =>
        toast.error(err?.response?.data?.message || "Failed to load data")
      )
      .finally(() => setLoadingTx(false));
  }, [customerId, dateRange, open, params.id]);

  // Delete Customer
  const handleDeleteCustomer = async () => {
    setOpenDeleteDialog(true);
  };

  const customFilterRanges = [
    {
      label: 'This Week', // Today to last Sunday
      value: [dayjs().startOf('week').toDate(), dayjs().toDate()]
    },
    {
      label: 'Last Week', // Last Sun to Last Sat
      value: [
        dayjs().subtract(1, 'week').startOf('week').toDate(),
        dayjs().subtract(1, 'week').endOf('week').toDate()
      ]
    },
    {
      label: 'This Month', // April (Current)
      value: [dayjs().startOf('month').toDate(), dayjs().toDate()]
    },
    {
      label: 'Last Month', // March (Previous)
      value: [
        dayjs().subtract(1, 'month').startOf('month').toDate(),
        dayjs().subtract(1, 'month').endOf('month').toDate()
      ]
    }
  ];

  const confirmDeleteCustomer = async () => {
    try {
      const response = await axiosInstance.delete(`/customers/${customerId}`);
      toast.success(response.data.message || "Customer deleted successfully");
      navigate(-1); // Go back to customers list
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete customer");
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const cancelDeleteCustomer = () => {
    setOpenDeleteDialog(false);
  };

  /** Transaction Form */
  const {
    register,
    watch,
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(transactionSchema),
    defaultValues: TRANSACTION_FORM_DEFAULTS,
  });

  const {
    fields: salesFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "sales",
  });

  const trayForm = useFieldArray({
    control,
    name: "trays",
  });

  const damageRefundForm = useFieldArray({
    control,
    name: "damageRefunds",
  });

  const stockGiven = watch("stockGiven");
  const paymentDone = watch("paymentDone");
  const traysReturned = watch("traysReturned");
  const discountGiven = watch("discountGiven");
  const labourEnabled = watch("labourCharges.enabled");
  const labourCost = watch("labourCharges.costPerItem");
  const labourQty = watch("labourCharges.numberOfItems");
  const damageRefundEnabled = watch("damageRefund.enabled");
  const damageRefunds = watch("damageRefunds") || [];
  const salesValues = watch("sales") || [];
  const watchedTransaction = watch();

  const totalSalesQty = (salesValues || []).reduce(
    (sum, sale) => sum + (Number(sale?.numberOfItems) || 0),
    0
  );

  React.useEffect(() => {
    if (!labourEnabled) return;

    if (totalSalesQty > 0) {
      setValue("labourCharges.numberOfItems", totalSalesQty, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [labourEnabled, totalSalesQty, setValue]);

  React.useEffect(() => {
    if (!discountGiven) {
      setValue("discount", undefined, {
        shouldValidate: false,
        shouldDirty: true,
      });
      clearErrors("discount");
    }
  }, [discountGiven, setValue, clearErrors]);

  React.useEffect(() => {
    if (!paymentDone) {
      setValue("paymentAmount", undefined, {
        shouldValidate: false,
        shouldDirty: true,
      });
      clearErrors("paymentAmount");
    }
  }, [paymentDone, setValue, clearErrors]);

  React.useEffect(() => {
    if (!traysReturned) {
      setValue(
        "trays",
        [
          {
            trayTypeId: undefined,
            numberOfTrays: undefined,
            excludePayment: false,
          },
        ],
        { shouldValidate: false, shouldDirty: true }
      );
      clearErrors("trays");
    }
  }, [traysReturned, setValue, clearErrors]);

  React.useEffect(() => {
    if (!stockGiven) {
      setValue(
        "sales",
        [
          {
            itemName: "",
            trayTypeId: undefined,
            numberOfItems: undefined,
            costPerItem: undefined,
          },
        ],
        { shouldValidate: false, shouldDirty: true }
      );
      clearErrors("sales");
    }
  }, [stockGiven, setValue, clearErrors]);

  React.useEffect(() => {
    // 1. If disabled, clear and exit.
    if (!damageRefundEnabled) {
      // Only clear if there is actually data to avoid unnecessary renders
      if (damageRefunds && damageRefunds.length > 0) {
        setValue("damageRefunds", [], {
          shouldValidate: false,
          shouldDirty: true,
        });
        clearErrors("damageRefunds");
      }
      return;
    }

    // 2. Initial item append if empty
    if (damageRefundEnabled && (!Array.isArray(damageRefunds) || damageRefunds.length === 0)) {
      damageRefundForm.append({
        selectedSaleIndex: undefined,
        numberOfItems: undefined,
        costPerItem: undefined,
        total: undefined,
      });
      return;
    }

    // 3. Loop through refunds and update ONLY if values are different
    damageRefunds.forEach((refund, index) => {
      const selectedSaleIndex = Number.isFinite(Number(refund.selectedSaleIndex))
        ? Number(refund.selectedSaleIndex)
        : undefined;
      
      const selectedSale = Number.isFinite(selectedSaleIndex)
        ? salesValues[selectedSaleIndex]
        : null;

      const selectedTrayType = selectedSale?.trayTypeId && trayTypes.length
        ? trayTypes.find(t => (t.trayTypeId === selectedSale.trayTypeId || t.id === selectedSale.trayTypeId))
        : null;

      // Calculations
      const computedCost = selectedSale
        ? Math.max(Number(selectedSale.costPerItem || 0) - Number(selectedTrayType?.price || 0), 0)
        : undefined;
      
      const qty = Number(refund.numberOfItems || 0);
      const computedTotal = (computedCost !== undefined && qty > 0) ? qty * computedCost : undefined;

      // --- STARK CHANGE DETECTION (Prevents Infinite Loops) ---
      
      // Update Cost Per Item
      if (computedCost !== undefined && Number(refund.costPerItem) !== computedCost) {
        setValue(`damageRefunds.${index}.costPerItem`, computedCost, { shouldValidate: true });
      }

      // Update Total
      if (computedTotal !== undefined && Number(refund.total) !== computedTotal) {
        setValue(`damageRefunds.${index}.total`, computedTotal);
      }

      // Validation: Ensure Qty doesn't exceed original sale items
      if (refund.numberOfItems && selectedSale && qty > Number(selectedSale.numberOfItems || 0)) {
        const maxAllowed = Number(selectedSale.numberOfItems || 0);
        if (Number(refund.numberOfItems) !== maxAllowed) {
          setValue(`damageRefunds.${index}.numberOfItems`, maxAllowed, { shouldValidate: true });
        }
      }
    });

    // Dependency Optimization: 
    // We stringify damageRefunds so the effect only triggers on VALUE changes, 
    // not just because the array reference changed.
  }, [
    damageRefundEnabled, 
    JSON.stringify(damageRefunds), 
    salesValues, 
    trayTypes, 
    setValue, 
    clearErrors
  ]);

  let filteredTrayTypes = [];
  if (Array.isArray(customerDetails?.trayBalance)) {
    filteredTrayTypes = customerDetails.trayBalance.filter(
      (type) => type.count > 0
    );
  }

  /** Payment Form */
  const paymentForm = useForm({
    resolver: yupResolver(paymentSchema),
    mode: "onTouched",
    defaultValues: {
      amount: "",
      paymentDate: null,
      paymentMethod: "",
      referenceId: "",
    },
  });

  const activeTrayTypes = trayTypes.filter((el) => el.status !== false);

  const closeTransactionPopup = () => {
    setEditTransaction(false);
    setSelectedTransaction(null);

    reset(TRANSACTION_FORM_DEFAULTS, {
      keepDirty: false,
      keepTouched: false,
    });
    setExcludePayment(false);
    setOpen(false);
  };

  const compareTransactions = (a, b) => {
    // Compare by calendar day so same-day transactions fall back to creation time
    const dayOf = (t) => dayjs(t?.dateOfTransaction).startOf("day").valueOf();
    const dateDifference = dayOf(a) - dayOf(b);
    if (Number.isFinite(dateDifference) && dateDifference) return dateDifference;

    const createdDifference =
      new Date(a?.createdAt).getTime() - new Date(b?.createdAt).getTime();
    if (Number.isFinite(createdDifference) && createdDifference) {
      return createdDifference;
    }

    return String(a?._id || "").localeCompare(String(b?._id || ""));
  };
  const orderedTransactions = [...transactionList].sort(compareTransactions);
  const orderedAllTransactions = [
    ...(allTransactions.length > 0 ? allTransactions : transactionList),
  ].sort(compareTransactions);
  const paginatedTransactions = [...orderedTransactions].reverse().slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getTrayBalanceCount = (trayTypeId) => {
    const tray = customerDetails?.trayBalance?.find((t) => t.id === trayTypeId);
    return tray ? Number(tray.count) : 0;
  };

  const selectedTrayTypeIds = watch("trays")
    ?.map((t) => t?.trayTypeId)
    .filter(Boolean);

  // Flattens transactions into ledger rows with a running balance.
  const buildLedger = (txs) => {
    const sortedTransactions = [...txs].sort(compareTransactions);
    const firstTransaction = sortedTransactions[0];
    const openingBalance = firstTransaction
      ? orderedAllTransactions
          .filter((tx) => compareTransactions(tx, firstTransaction) < 0)
          .reduce(
            (sum, tx) =>
              sum + calculatePastTransactionsBalance(tx, [tx], trayTypes),
            0
          )
      : 0;
    let runningBalance = openingBalance;
    let currentTx = null;
    const rows = [];
    const addRow = ({
      date,
      description = "",
      item = "",
      rate = "",
      quantity = "",
      amount = 0,
      payment = 0,
      displayRate = rate,
      displayAmount = amount,
      excluded = false,
    }) => {
      runningBalance += Number(amount || 0) - Number(payment || 0);
      rows.push({
        tx: currentTx,
        date,
        description,
        item,
        rate,
        displayRate,
        displayAmount,
        excluded,
        quantity,
        amount: Number(amount) || 0,
        payment: Number(payment) || 0,
        balance: runningBalance,
      });
    };

    sortedTransactions.forEach((tx) => {
      currentTx = tx;
      const date = tx.dateOfTransaction;
      (tx.sales || []).forEach((sale) => {
        const quantity = Number(sale?.numberOfItems) || 0;
        const rate = Number(sale?.costPerItem) || 0;
        const savedTotal = Number(sale?.total);
        addRow({
          date,
          description: "Sales",
          item: sale?.itemName,
          rate: sale?.costPerItem ?? "",
          quantity: sale?.numberOfItems ?? "",
          amount:
            sale?.total != null && Number.isFinite(savedTotal)
              ? savedTotal
              : quantity * rate,
        });
      });

      if (tx.labourCharges?.enabled || Number(tx.labourCharges?.total) > 0) {
        addRow({
          date,
          description: "Sales",
          item: "Loading Charges",
          rate: tx.labourCharges?.costPerItem ?? "",
          quantity: tx.labourCharges?.numberOfItems ?? "",
          amount: tx.labourCharges?.total,
        });
      }

      if (Number(tx.discount) > 0) {
        addRow({ date, description: "Discount", amount: -Number(tx.discount) });
      }

      const damageRefunds =
        Array.isArray(tx.damageRefunds) && tx.damageRefunds.length > 0
          ? tx.damageRefunds
          : tx.damageRefund?.enabled || tx.damageRefund?.total
          ? [tx.damageRefund]
          : [];
      damageRefunds.forEach((refund) => {
        addRow({
          date,
          description: "Damage Refund",
          item: refund?.itemName,
          quantity: refund?.numberOfItems ?? "",
          payment: refund?.total,
        });
      });

      (tx.trays || []).forEach((tray) => {
        const trayType = trayTypes.find(
          (type) =>
            type.trayTypeId === tray.trayTypeId || type.id === tray.trayTypeId
        );
        const rate = Number(tray.costPerTray) || Number(trayType?.price) || 0;
        const quantity = Number(tray.numberOfTrays) || 0;
        const trayName = trayType?.name || trayType?.label || "";
        addRow({
          date,
          description: trayName
            ? `TRY PAYMENT ${trayName}`
            : `TRY PAYMENT ${Math.round(rate).toLocaleString("en-IN")}`,
          quantity,
          displayRate: rate,
          displayAmount: quantity * rate,
          excluded: !!tray.excludePayment,
          payment: tray.excludePayment ? 0 : quantity * rate,
        });
      });

      if (Number(tx.paymentAmount) > 0) {
        addRow({
          date,
          description: "Payment Received",
          payment: tx.paymentAmount,
        });
      }
    });

    const openingDate = dateRange.startDate
      ? dayjs(dateRange.startDate).subtract(1, "day")
      : firstTransaction
      ? dayjs(firstTransaction.dateOfTransaction).subtract(1, "day")
      : dayjs();

    return { rows, openingBalance, openingDate, closingBalance: runningBalance };
  };

  const printPDF = () => {
    const printWindow = window.open("", "_blank");
    const rangeLabel =
      dateRange?.startDate && dateRange?.endDate
        ? ` - ${dayjs(dateRange.startDate).format("DD MMM YYYY")} - ${dayjs(dateRange.endDate).format("DD MMM YYYY")}`
        : "";
    printWindow.document.write(
      buildBalanceSheetHtml({
        title: `Balance Sheet - ${customerDetails?.name || ""}${rangeLabel}`,
        logo: LOGO_BASE64,
        employerName: user?.employerName,
        branchName: customerDetails?.branchName,
        customer: customerDetails,
        transactions: transactionList,
        allTransactions,
        trayTypes,
      })
    );
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };
  const closePaymentPopup = () => {
    paymentForm.reset();
    setOpenPayment(false);
  };

  const clearDateFilter = () => {
    setDateRange({
      startDate: null,
      endDate: null,
    });
  };

  const formatCurrency = (amount) => {
    return Math.round(Number(amount || 0)).toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    });
  };

  const formatIndianAmount = (amount) => {
    const numericAmount = Number(amount);
    return amount == null || !Number.isFinite(numericAmount)
      ? "-"
      : numericAmount.toLocaleString("en-IN");
  };

  /** Add Transaction */
  const addTransaction = async (data) => {
    let dateOfTransaction = data.dateOfTransaction;

    // If no date is provided, use current date as fallback
    if (!dateOfTransaction) {
      dateOfTransaction = new Date();
    }

    // Build payload with all checkbox states and their corresponding fields
    const payload = {
      customerId: params?.id,
      dateOfTransaction: dateOfTransaction,
      paymentAmount: data?.paymentAmount,
    };

    if (stockGiven) {
      payload.sales = data?.sales;
    }

    if (data?.labourCharges?.enabled) {
      payload.labourCharges = {
        enabled: true,
        numberOfItems: totalSalesQty,
        costPerItem: data.labourCharges.costPerItem,
        total:
          Number(totalSalesQty || 0) *
          Number(data.labourCharges.costPerItem || 0),
      };
    }

    if (traysReturned) {
      payload.trays = data.trays.map((tray) => {
        const selectedTrayType = trayTypes.find(
          (type) =>
            type.trayTypeId === tray.trayTypeId || type.id === tray.trayTypeId
        );
        tray.costPerTray = excludePayment ? 0 : selectedTrayType?.price || 0;
        tray.excludePayment = excludePayment;
        return tray;
      });
    }

    if (discountGiven) {
      payload.discount = data.discount;
    }

    if (editTransaction && data?.damageRefund?.enabled) {
      const refundRows = Array.isArray(data.damageRefunds)
        ? data.damageRefunds
        : [];
      const damageRefundsPayload = [];

      refundRows.forEach((refund) => {
        const rowIndex = Number(refund.selectedSaleIndex);
        const selectedSale = Array.isArray(data.sales)
          ? data.sales[rowIndex]
          : null;
        const selectedTrayType = selectedSale?.trayTypeId
          ? trayTypes.find(
              (type) =>
                type.trayTypeId === selectedSale.trayTypeId ||
                type.id === selectedSale.trayTypeId
            )
          : null;
        const refundCost = Math.max(
          Number(selectedSale?.costPerItem || 0) -
            Number(selectedTrayType?.price || 0),
          0
        );
        const refundQty = Number(refund.numberOfItems || 0);

        if (selectedSale && refundQty > 0 && refundCost > 0) {
          damageRefundsPayload.push({
            itemName: selectedSale.itemName || "",
            selectedSaleIndex: rowIndex,
            numberOfItems: refundQty,
            costPerItem: refundCost,
            total: refundQty * refundCost,
          });
        }
      });

      if (damageRefundsPayload.length > 0) {
        payload.damageRefunds = damageRefundsPayload;
      }
    }

    let path = "transactions/create";

    if (editTransaction) {
      path = "transactions/update/" + selectedTransaction._id;
    }

    try {
      setSaving(true);
      const response = await axiosInstance.post(path, payload);
      toast.success(
        response?.data?.message || "Transaction saved successfully"
      );

      // Show receipt only for new transactions (not edits)
      if (!editTransaction) {
        setReceiptTransaction(response?.data?.transaction);
        setShowReceipt(true);
      }

      closeTransactionPopup();
      setSaving(false);
    } catch (err) {
      setSaving(false);
      console.error("Error submitting transaction:", err); // Debug log
      toast.error(err?.response?.data?.message || "Failed to save transaction");
    }
  };

  const val = (v) => v ?? "-";

  /*Update Payment */
  const updatePayment = async (data) => {
    const payload = {
      transactionId: selectedTransactionId,
      amount: data.amount,
      paymentDate: data.paymentDate,
      paymentMethod: data.paymentMethod,
      referenceId: data.referenceId,
    };

    try {
      const response = await axiosInstance.post(
        "transactions/update-payment",
        payload
      );
      toast.success(response?.data?.message || "Payment updated successfully");
      closePaymentPopup();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update payment");
    }
  };

  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const totalTransactionAmount = paginatedTransactions.reduce((sum, tx) => {
    const salesTotal = (tx.sales || []).reduce((tSum, sale) => {
      return tSum + toNumber(sale?.total);
    }, 0);
    return sum + salesTotal;
  }, 0);

  const discountAmount = transactionList.reduce((sum, tx) => {
    return sum + (toNumber(tx?.discount) || 0);
  }, 0);

  const grandTotalTransactionAmount = transactionList.reduce((sum, tx) => {
    const salesTotal = (tx.sales || []).reduce((tSum, sale) => {
      return tSum + toNumber(sale?.total);
    }, 0);
    return sum + salesTotal;
  }, 0);

  const damageRefund = transactionList.reduce((sum, tx) => {
    const damageTotal = (tx.damageRefunds || [tx.damageRefund] || []).reduce(
      (tSum, damage) => {
        return tSum + toNumber(damage?.total);
      },
      0
    );
    return sum + damageTotal;
  }, 0);

  const labourCharges = transactionList.reduce((sum, tx) => {
    return sum + toNumber(tx?.labourCharges?.total || 0);
  }, 0);

  const totalPaidAmount = paginatedTransactions.reduce((sum, tx) => {
    return sum + toNumber(tx?.paymentAmount);
  }, 0);

  const grandTotalPaidAmount = transactionList.reduce((sum, tx) => {
    return sum + toNumber(tx?.paymentAmount);
  }, 0);

  const trayPaymentAmount = paginatedTransactions.reduce((sum, tx) => {
    const traysTotal = (tx.trays || []).reduce((tSum, tray) => {
      if (excludePayment) return tSum;

      return tSum + toNumber(tray?.numberOfTrays) * toNumber(tray?.costPerTray);
    }, 0);
    return sum + traysTotal;
  }, 0);

  const grandTrayPaymentAmount = transactionList.reduce((sum, tx) => {
    const traysTotal = (tx.trays || []).reduce((tSum, tray) => {
      return tSum + toNumber(tray?.numberOfTrays) * toNumber(tray?.costPerTray);
    }, 0);

    return sum + traysTotal;
  }, 0);

  const generateReceiptHTML = (tx, customerDetails, accountBalanceData) => {
    const sales = tx.sales || [];
    const trays = tx.trays || [];
    const labourCharges = tx.labourCharges || {};
    const damageRefunds =
      Array.isArray(tx.damageRefunds) && tx.damageRefunds.length > 0
        ? tx.damageRefunds
        : tx.damageRefund?.enabled
        ? [tx.damageRefund]
        : [];
    const paymentReceived = Number(tx.paymentAmount) || 0;

    const salesTotal = sales.reduce(
      (sum, s) => sum + (Number(s.numberOfItems) * Number(s.costPerItem) || 0),
      0
    );
    const labourTotal = Number(
      labourCharges.enabled ? labourCharges.total || 0 : 0
    );
    const refundAmount = damageRefunds.reduce(
      (sum, refund) => sum + Number(refund.total || 0),
      0
    );
    const discountAmount = Number(tx.discount || 0);
    const trayPaymentTotal = trays.reduce(
      (sum, t) => sum + (Number(t.numberOfTrays) * Number(t.costPerTray) || 0),
      0
    );
    const totalCredits = paymentReceived + trayPaymentTotal;
    const transactionBalance =
      salesTotal + labourTotal - refundAmount - totalCredits - discountAmount;
    const accountBalance = accountBalanceData?.totalAmountDue || 0;

    return `
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .receipt-container { border: 1px solid #ccc; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            th, td { border: 1px solid #333; padding: 8px; }
            .primary-color { color: #1976d2; }
            .right { text-align: right; }
            /* Add the rest of your receipt styles here */
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <h2>${user?.employerName}</h2>
            <p>Customer: ${customerDetails?.name}</p>
            <p>Date: ${new Date(tx.dateOfTransaction).toLocaleDateString(
              "en-IN"
            )}</p>
            
            <table>
              <thead><tr><th>Item</th><th class="right">Qty</th><th class="right">Amount</th></tr></thead>
              <tbody>
                ${sales
                  .map(
                    (s) =>
                      `<tr><td>${s.itemName}</td><td class="right">${s.numberOfItems}</td><td class="right">₹${formatCurrency(s.total)}</td></tr>`
                  )
                  .join("")}
                ${
                  labourCharges.enabled
                    ? `<tr><td>Loading Charges</td><td class="right">${labourCharges.numberOfItems}</td><td class="right">₹${formatCurrency(labourTotal)}</td></tr>`
                    : ""
                }
                ${damageRefunds
                  .map(
                    (refund) =>
                      `<tr><td>Damage Refund: ${refund.itemName}</td><td class="right">${refund.numberOfItems}</td><td class="right">- ₹${formatCurrency(refund.total)}</td></tr>`
                  )
                  .join("")}
              </tbody>
            </table>

            <div class="right">
              <p><strong>Total Due: ₹${formatCurrency(
                salesTotal + labourTotal - refundAmount
              )}</strong></p>
              ${
                discountAmount > 0
                  ? `<p><strong>Discount: ₹${formatCurrency(discountAmount)}</strong></p>`
                  : ""
              }
              <p><strong>Total Paid: ₹${formatCurrency(totalCredits)}</strong></p>
              <hr/>
              <p><strong>Balance: ₹${formatCurrency(transactionBalance)}</strong></p>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const openEditTransactionPopup = (tx) => {
    setEditTransaction(true);
    setSelectedTransaction(tx);

    const hasSales = Array.isArray(tx.sales) && tx.sales.length > 0;
    const hasTrays = Array.isArray(tx.trays) && tx.trays.length > 0;
    const hasPayment = !!tx.paymentAmount;
    const hasDiscount = !!tx.discount;

    let trayPaymentExcluded = tx.trays.some((t) => t.excludePayment);
    const damageRefundItemIndex =
      hasSales && tx.damageRefund?.itemName
        ? tx.sales.findIndex(
            (sale) => sale.itemName === tx.damageRefund.itemName
          )
        : -1;

    setExcludePayment(trayPaymentExcluded);

    reset({
      stockGiven: hasSales,
      traysReturned: hasTrays,
      discountGiven: hasDiscount,
      paymentDone: hasPayment,

      dateOfTransaction: tx.dateOfTransaction
        ? new Date(tx.dateOfTransaction)
        : null,

      paymentAmount: tx.paymentAmount || "",
      discount: tx.discount || "",

      sales: hasSales
        ? tx.sales.map((s) => ({
            itemName: s.itemName,
            numberOfItems: s.numberOfItems,
            costPerItem: s.costPerItem,
            trayTypeId: s.trayTypeId ?? undefined,
          }))
        : [
            {
              itemName: "",
              numberOfItems: undefined,
              costPerItem: undefined,
              trayTypeId: undefined,
            },
          ],

      labourCharges: tx.labourCharges
        ? {
            enabled: Boolean(tx.labourCharges.enabled),
            numberOfItems: tx.labourCharges.numberOfItems,
            costPerItem: tx.labourCharges.costPerItem,
          }
        : {
            enabled: false,
            numberOfItems: undefined,
            costPerItem: undefined,
          },

      damageRefund: {
        enabled: Boolean(
          (Array.isArray(tx.damageRefunds) && tx.damageRefunds.length > 0) ||
            tx.damageRefund?.enabled
        ),
      },

      damageRefunds:
        Array.isArray(tx.damageRefunds) && tx.damageRefunds.length > 0
          ? tx.damageRefunds.map((refund) => ({
              selectedSaleIndex:
                typeof refund.selectedSaleIndex === "number"
                  ? refund.selectedSaleIndex
                  : damageRefundItemIndex >= 0
                  ? damageRefundItemIndex
                  : undefined,
              numberOfItems: refund.numberOfItems,
              costPerItem: refund.costPerItem,
              total: refund.total,
            }))
          : tx.damageRefund?.enabled
          ? [
              {
                selectedSaleIndex:
                  damageRefundItemIndex >= 0
                    ? damageRefundItemIndex
                    : undefined,
                numberOfItems: tx.damageRefund.numberOfItems,
                costPerItem: tx.damageRefund.costPerItem,
                total: tx.damageRefund.total,
              },
            ]
          : [
              {
                selectedSaleIndex: undefined,
                numberOfItems: undefined,
                costPerItem: undefined,
                total: undefined,
              },
            ],

      trays: hasTrays
        ? tx.trays.map((t) => ({
            numberOfTrays: t.numberOfTrays,
            trayTypeId: t.trayTypeId,
            excludePayment: t.excludePayment,
          }))
        : [
            {
              numberOfTrays: undefined,
              trayTypeId: undefined,
              excludePayment: false,
            },
          ],
    });

    setOpen(true);
  };

  // Slip data shared by the printed receipt, receipt popup and WhatsApp message
  const getReceiptSlip = (tx) => {
    const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    const history = [
      ...(allTransactions.length > 0 ? allTransactions : transactionList),
    ];
    const isSame = (t) =>
      (t?._id && tx?._id && String(t._id) === String(tx._id)) || t === tx;
    if (!history.some(isSame)) history.push(tx);
    history.sort(compareTransactions);

    // Sum of every transaction that comes before this one (same order as the Balance Sheet)
    const openingBalance = history
      .filter((t) => !isSame(t) && compareTransactions(t, tx) < 0)
      .reduce(
        (sum, t) => sum + calculatePastTransactionsBalance(t, [t], trayTypes),
        0
      );
    const saleTotal = (sale) =>
      sale?.total != null && Number.isFinite(Number(sale.total))
        ? Number(sale.total)
        : num(sale?.numberOfItems) * num(sale?.costPerItem);

    const sales = (tx.sales || []).map((s) => ({
      name: s.itemName || "Item",
      label: s.itemName || "Item",
      qty: s.numberOfItems,
      rate: s.costPerItem,
      amount: saleTotal(s),
    }));

    const loadingCharges =
      tx.labourCharges?.enabled || num(tx.labourCharges?.total) > 0
        ? {
            numberOfItems: tx.labourCharges?.numberOfItems,
            costPerItem: tx.labourCharges?.costPerItem,
            amount: num(tx.labourCharges?.total),
          }
        : null;

    const damageRefunds = (
      Array.isArray(tx.damageRefunds) && tx.damageRefunds.length > 0
        ? tx.damageRefunds
        : tx.damageRefund?.enabled || tx.damageRefund?.total
        ? [tx.damageRefund]
        : []
    ).map((r) => ({
      name: r.itemName,
      label: `Damage Refund${r.itemName ? ` (${r.itemName})` : ""}`,
      numberOfItems: r.numberOfItems,
      costPerItem: r.costPerItem,
      amount: num(r.total),
    }));

    const discount = num(tx.discount);
    const paymentAmount = num(tx.paymentAmount);

    const trays = (tx.trays || []).map((t) => {
      const type = trayTypes.find(
        (tt) => tt.trayTypeId === t.trayTypeId || tt.id === t.trayTypeId
      );
      const price = num(t.costPerTray) || num(type?.price);
      return {
        name: type?.name || type?.label || "Tray",
        label: `Tray Returned${type?.label ? ` (${type.label})` : ""}`,
        numberOfItems: t.numberOfTrays,
        qty: t.numberOfTrays,
        costPerTray: price,
        rate: price,
        amount: t.excludePayment ? 0 : num(t.numberOfTrays) * price,
        excluded: !!t.excludePayment,
      };
    });

    const salesTotal = sales.reduce((sum, s) => sum + s.amount, 0);
    const loadingTotal = loadingCharges ? loadingCharges.amount : 0;
    const refundsTotal = damageRefunds.reduce((sum, r) => sum + r.amount, 0);
    const netSales = salesTotal + loadingTotal - refundsTotal - discount;
    const subTotal = openingBalance + netSales;
    const trayPaymentTotal = trays.reduce((sum, t) => sum + t.amount, 0);
    const totalCredits = paymentAmount + trayPaymentTotal;
    const closingBalance = subTotal - totalCredits;

    return {
      openingBalance,
      sales,
      loadingCharges,
      damageRefunds,
      discount,
      salesTotal,
      netSales,
      subTotal,
      paymentAmount,
      trays,
      trayPaymentTotal,
      totalCredits,
      closingBalance,
    };
  };

  const slipAmount = (amount) => {
    const n = Math.round(Number(amount) || 0);
    return `${n < 0 ? "-" : ""}${Math.abs(n).toLocaleString("en-IN")}`;
  };

  const shareTransactionToWhatsapp = (tx) => {
    const slip = getReceiptSlip(tx);
    const receiptUrl = `${window.location.origin}/receipt/${tx._id}`;
    const line = (s) => {
      const amt = s.excluded ? "No Cost" : slipAmount(s.amount);
      return s.qty != null && s.rate != null
        ? `${s.name || s.label} ${s.qty} x ${slipAmount(s.rate)} = ${amt}`
        : `${s.name || s.label} = ${amt}`;
    };

    const hasSales =
      (slip.sales && slip.sales.length > 0) ||
      !!slip.loadingCharges ||
      (slip.damageRefunds && slip.damageRefunds.length > 0);

    const salesTotalBeforeDiscount =
      Number(slip.subTotal || 0) + Number(slip.discount || 0);

    const lines = [
      `*${customerDetails?.name || "N/A"}*`,
      new Date(tx.dateOfTransaction).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      }),
      "",
      `Opening Balance                               ${slipAmount(slip.openingBalance)}`,
    ];

    if (hasSales) {
      lines.push(
        "-------------------------------------",
        "*Sales*",
        ...slip.sales.map((s) => {
          const calc = s.qty != null && s.rate != null ? `${s.qty} X ${slipAmount(s.rate)}` : "";
          return `${s.name || s.label}   ${calc}   ${slipAmount(s.amount)}`;
        })
      );

      if (slip.loadingCharges) {
        const lQty = slip.loadingCharges.numberOfItems;
        const lRate = slip.loadingCharges.costPerItem;
        const calc = lQty != null && lRate != null ? `${lQty} X ${slipAmount(lRate)}` : "";
        lines.push(`Loading Charges   ${calc}   ${slipAmount(slip.loadingCharges.amount)}`);
      }

      if (slip.damageRefunds && slip.damageRefunds.length > 0) {
        slip.damageRefunds.forEach((r) => {
          const calc = r.numberOfItems != null && r.costPerItem != null ? `${r.numberOfItems} X ${slipAmount(r.costPerItem)}` : "";
          lines.push(`Return${r.name ? ` (${r.name})` : ""}   ${calc}   ${slipAmount(r.amount)}`);
        });
      }

      lines.push(
        "-------------------------------------",
        `Total                                         ${slipAmount(salesTotalBeforeDiscount)}`
      );
    }

    if (slip.discount > 0) {
      lines.push(
        "-------------------------------------",
        "*Discount* (Taken off from bill)",
        `Discount                                      − ${slipAmount(slip.discount)}`
      );
    }

    if (slip.paymentAmount > 0) {
      lines.push(
        "-------------------------------------",
        "*Payment*",
        `Cash / Online Payment                         ${slipAmount(slip.paymentAmount)}`
      );
    }

    if (slip.trays && slip.trays.length > 0) {
      const allExcluded = slip.trays.every((t) => t.excluded);
      const trayTotalStr = allExcluded ? "No Cost" : slipAmount(slip.trayPaymentTotal);
      lines.push(
        "-------------------------------------",
        "*Tray Payment*",
        ...slip.trays.map((t, idx) => {
          const amt = t.excluded ? "No Cost" : slipAmount(t.amount);
          const formulaWithTotal = `${t.name}   ${t.numberOfItems} X ${slipAmount(t.costPerTray)} = ${amt}`;
          return idx === 0
            ? `${formulaWithTotal}        ${trayTotalStr}`
            : formulaWithTotal;
        })
      );
    }

    lines.push(
      "-------------------------------------",
      `Due Balance                                   ${slipAmount(slip.closingBalance)}`,
      "",
      `🔗 View Receipt: ${receiptUrl}`,
      "",
      "Thank you!"
    );

    const message = lines.join("\n");

    const mobile = customerDetails?.mobile || "";
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl =
      mobile && mobile !== "0000000000"
        ? `https://api.whatsapp.com/send?phone=91${mobile}&text=${encodedMessage}`
        : `https://api.whatsapp.com/send?text=${encodedMessage}`;

    window.open(whatsappUrl, "_blank");
  };

  const shareTransactions = () => {
    const sDate = dateRange.startDate ? dayjs(dateRange.startDate).format("YYYY-MM-DD") : "";
    const eDate = dateRange.endDate ? dayjs(dateRange.endDate).format("YYYY-MM-DD") : "";
  
    const listUrl = `${window.location.origin}/transactions/anonomous?startDate=${sDate}&endDate=${eDate}&customerId=${params?.id}`;
    const totalDue = formatIndianAmount(Math.round((grandTotalTransactionAmount + labourCharges - (grandTotalPaidAmount + grandTrayPaymentAmount + discountAmount + damageRefund)) || 0));
    const message = `*Transactions ${dateRange?.startDate && dateRange?.endDate ? `- ${dateRange.startDate} to ${dateRange.endDate}` : ""}*\n\nCustomer: ${
      customerDetails?.name || "N/A"
    }\nBranch: ${
      customerDetails?.branchName || customerDetails?.address || "N/A"
    }\n\n*Total Balance Due: ₹${totalDue}*\n\n🔗 View Transactions: ${listUrl}\n\nThank you!`;

    const mobile = customerDetails?.mobile || "";
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl =
      mobile && mobile !== "0000000000"
        ? `https://api.whatsapp.com/send?phone=91${mobile}&text=${encodedMessage}`
        : `https://api.whatsapp.com/send?text=${encodedMessage}`;

    window.open(whatsappUrl, "_blank");
  }

  const buildTransactionHTML = (tx) => {
    const slip = getReceiptSlip(tx);
    const esc = (v) =>
      String(v ?? "").replace(/[&<>"']/g, (c) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c]);
    const date = new Date(tx.dateOfTransaction).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });

    const tableHtml = buildReceiptTable({
      openingBalance: slip.openingBalance,
      sales: slip.sales,
      loadingCharges: slip.loadingCharges,
      damageRefunds: slip.damageRefunds,
      discount: slip.discount,
      subTotal: slip.subTotal,
      paymentAmount: slip.paymentAmount,
      trays: slip.trays,
      trayPaymentTotal: slip.trayPaymentTotal,
      closingBalance: slip.closingBalance,
    });

    return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8" />
      <title>${esc(customerDetails?.name)} - ${date}</title>
      <style>
        ${receiptSlipCss()}
        ${receiptTableCss()}
      </style>
    </head>
    <body>
      <div class="slip">
        <div class="header">
          <div class="brand">
            <img src="${LOGO_BASE64}" />
            <div>
              <div class="company">${esc(user?.employerName)}</div>
              <div class="branch">${esc(customerDetails?.branchName || "Transaction Receipt")}</div>
              <div class="proprietor">Proprietor: <strong>Maddireddy Ramesh</strong> &bull; Contact: <strong>+91 76666 77779</strong></div>
            </div>
          </div>
          <div class="hdate">Date<br /><strong>${date}</strong></div>
        </div>
        <div class="meta">
          <div class="customer">${esc(customerDetails?.name || "N/A")}</div>
          <div class="location">${esc(customerDetails?.location || customerDetails?.address || "")}</div>
        </div>
        ${tableHtml}
      </div>
    </body>
    </html>
  `;
  };

  const printTransaction = (tx) => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(buildTransactionHTML(tx));
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const fmtInr = (v) =>
    `₹ ${Math.round(Number(v) || 0).toLocaleString("en-IN")}`;

  const amountDue =
    (grandTotalTransactionAmount || 0) +
    (labourCharges || 0) -
    ((grandTotalPaidAmount || 0) +
      (grandTrayPaymentAmount || 0) +
      (discountAmount || 0) +
      (damageRefund || 0));

  const summaryMetrics = [
    {
      label: "Transaction Value",
      value: fmtInr(grandTotalTransactionAmount),
      color: "#1e293b",
      bg: "#f8fafc",
      border: "#e2e8f0",
    },
    {
      label: "Discount",
      value: fmtInr(discountAmount),
      color: "#0369a1",
      bg: "#f0f9ff",
      border: "#bae6fd",
    },
    {
      label: "Damage Refund",
      value: fmtInr(damageRefund),
      color: "#b91c1c",
      bg: "#fef2f2",
      border: "#fecaca",
    },
    {
      label: "Amount Paid",
      value: fmtInr((grandTotalPaidAmount || 0) + (grandTrayPaymentAmount || 0)),
      color: "#15803d",
      bg: "#f0fdf4",
      border: "#bbf7d0",
    },
    {
      label: "Amount Due",
      value: fmtInr(amountDue),
      color: "var(--primary-contrast, #fff)",
      bg: "var(--primary-gradient, var(--primary-color))",
      border: "var(--primary-color)",
      emphasize: true,
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      {/* Customer Summary */}
      {customerDetails && (
        <Paper
          elevation={0}
          sx={{
            flexShrink: 0,
            px: { xs: 2, md: 2.5 },
            py: { xs: 1.5, md: 1.75 },
            mb: 0,
            borderRadius: 0,
            borderBottom: "none",
            backgroundColor: "#fff",
          }}
        >
          <Box
            display="flex"
            flexDirection={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", lg: "center" }}
            gap={2}
          >
            <Box minWidth={0}>
              <Stack direction="row" alignItems="center" gap={1} mb={0.75}>
                <IconButton
                  onClick={() => navigate(-1)}
                  size="small"
                  sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 1.5,
                    color: "#374151",
                    "&:hover": { backgroundColor: "#f8fafc" },
                  }}
                >
                  <ArrowBack fontSize="small" />
                </IconButton>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    color: "#0f172a",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.2,
                    textTransform: "capitalize",
                  }}
                >
                  {customerDetails?.name || "—"}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                ml={{ xs: 0, sm: 5 }}
              >
                <Chip
                  size="small"
                  icon={<MdPhoneAndroid size={14} />}
                  label={customerDetails?.mobile || "N/A"}
                  sx={{
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    fontWeight: 500,
                    color: "#334155",
                    "& .MuiChip-icon": { color: "#64748b", ml: 0.75 },
                  }}
                />
                <Chip
                  size="small"
                  icon={<MdLocationOn size={14} />}
                  label={customerDetails?.location || "N/A"}
                  sx={{
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    fontWeight: 500,
                    color: "#334155",
                    textTransform: "capitalize",
                    "& .MuiChip-icon": { color: "#64748b", ml: 0.75 },
                  }}
                />

                {/* {user?.roleId !== 3 && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDeleteCustomer}
                    size="small"
                    startIcon={<FaTrash />}
                  >
                    Delete Customer
                  </Button>
                )} */}
              </Stack>
            </Box>

            <Box
              display="grid"
              gridTemplateColumns={{
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(5, minmax(0, 1fr))",
              }}
              gap={1.25}
              flex={1}
              maxWidth={{ lg: 820 }}
            >
              {summaryMetrics.map((metric) => (
                <Box
                  key={metric.label}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    border: `1px solid ${metric.border}`,
                    background: metric.bg,
                    textAlign: "left",
                    minWidth: 0,
                    boxShadow: metric.emphasize
                      ? "0 4px 14px var(--primary-shadow)"
                      : "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: 0.2,
                      textTransform: "capitalize",
                      color: metric.emphasize
                        ? "var(--primary-contrast-sub, rgba(255,255,255,0.85))"
                        : "#64748b",
                      mb: 0.35,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {metric.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 15, md: 17 },
                      fontWeight: 700,
                      color: metric.color,
                      fontVariantNumeric: "tabular-nums",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {metric.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      )}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          px: { xs: 1.5, md: 2 },
          pb: 1.5,
          pt: 1,
          gap: 1.25,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          gap={1.25}
          py={1.25}
          px={1.5}
          sx={{
            flexShrink: 0,
            backgroundColor: "#f8fafc",
            border: "1px solid #e5e7eb",
            borderRadius: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                color: "#0f172a",
                lineHeight: 1.2,
                textTransform: "capitalize",
                fontSize: "1.1rem",
              }}
            >
              Balance Sheet
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: "capitalize" }}
            >
              Transactions & account ledger
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1}
            alignItems="center"
            flexWrap="wrap"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
          >
            <Button
              variant="outlined"
              onClick={() => setOpenTrayModal(true)}
              size="small"
              startIcon={<Inventory2OutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "capitalize",
                fontWeight: 600,
                fontSize: 13,
                height: 36,
                px: 1.5,
                borderRadius: "10px",
                borderColor: "#e2e8f0",
                color: "#334155",
                backgroundColor: "#fff",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                "&:hover": {
                  borderColor: "var(--primary-color)",
                  backgroundColor: "var(--primary-light)",
                  color: "var(--primary-color)",
                  boxShadow: "0 4px 10px var(--primary-shadow)",
                },
              }}
            >
              Tray Balance
            </Button>

            <Button
              variant="outlined"
              onClick={() => navigate("/tray-history/" + params?.id)}
              size="small"
              startIcon={<HistoryOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "capitalize",
                fontWeight: 600,
                fontSize: 13,
                height: 36,
                px: 1.5,
                borderRadius: "10px",
                borderColor: "#e2e8f0",
                color: "#334155",
                backgroundColor: "#fff",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                "&:hover": {
                  borderColor: "var(--primary-color)",
                  backgroundColor: "var(--primary-light)",
                  color: "var(--primary-color)",
                  boxShadow: "0 4px 10px var(--primary-shadow)",
                },
              }}
            >
              Tray History
            </Button>

            <Box
              sx={{
                "& .rs-picker-toggle": {
                  height: "36px !important",
                  minHeight: "36px !important",
                  borderRadius: "10px !important",
                  borderColor: "#e2e8f0 !important",
                  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                  fontSize: "13px",
                },
                "& .rs-picker-toggle:hover, & .rs-picker-toggle-active": {
                  borderColor: "var(--primary-color) !important",
                },
              }}
            >
              <RangeFilter
                dateRange={dateRange}
                setDateRange={setDateRange}
                width={210}
                ranges={customFilterRanges}
              />
            </Box>

            <Tooltip title="Print Balance Sheet" arrow placement="top">
              <span>
                <IconButton
                  onClick={printPDF}
                  disabled={paginatedTransactions.length === 0}
                  aria-label="Print balance sheet"
                  sx={{
                    color: "var(--primary-color)",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    width: 36,
                    height: 36,
                    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                    transition: "all 0.2s ease",
                    "&.Mui-disabled": {
                      border: "1px solid #e5e7eb",
                      color: "#94a3b8",
                      boxShadow: "none",
                    },
                    "&:hover": {
                      backgroundColor: "var(--primary-light)",
                      borderColor: "var(--primary-color)",
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 10px var(--primary-shadow)",
                    },
                  }}
                >
                  <FaPrint size={15} />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Share on WhatsApp" arrow placement="top">
              <span>
                <IconButton
                  onClick={shareTransactions}
                  disabled={paginatedTransactions.length === 0}
                  aria-label="Share on WhatsApp"
                  sx={{
                    color: "#25D366",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    width: 36,
                    height: 36,
                    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                    transition: "all 0.2s ease",
                    "&.Mui-disabled": {
                      border: "1px solid #e5e7eb",
                      color: "#94a3b8",
                      boxShadow: "none",
                    },
                    "&:hover": {
                      backgroundColor: "#ecfdf5",
                      borderColor: "#86efac",
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 10px rgba(37, 211, 102, 0.25)",
                    },
                  }}
                >
                  <FaWhatsapp size={16} />
                </IconButton>
              </span>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
              size="small"
              sx={{
                textTransform: "capitalize",
                fontWeight: 700,
                fontSize: 13,
                height: 36,
                px: 1.75,
                borderRadius: "10px",
                boxShadow: "0 4px 12px var(--primary-shadow)",
                "&:hover": {
                  boxShadow: "0 6px 16px var(--primary-shadow)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Add New
            </Button>
          </Box>
        </Box>

        {/* Table */}

        {loadingTx ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              flex: 1,
              minHeight: 0,
              borderRadius: 2,
              border: "1px dashed #cbd5e1",
              backgroundColor: "#f8fafc",
            }}
          >
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading transactions...
            </Typography>
          </Box>
        ) : transactionList.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              flex: 1,
              minHeight: 0,
              px: 2,
              borderRadius: 2,
              border: "1px dashed #cbd5e1",
              backgroundColor: "#f8fafc",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: "#334155", mb: 0.5, textTransform: "capitalize" }}
            >
              No transactions found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add a new transaction or adjust the date filter.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              border: "1px solid #e5e7eb",
              overflow: "hidden",
              backgroundColor: "#fff",
            }}
          >
            <TableContainer
              component={Box}
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
              }}
            >
              {(() => {
                const ledger = buildLedger(paginatedTransactions);
                const groups = new Map();
                ledger.rows.forEach((r) => {
                  if (!groups.has(r.tx)) groups.set(r.tx, { tx: r.tx, rows: [] });
                  groups.get(r.tx).rows.push(r);
                });
                // Latest transaction on top; line order inside a transaction is kept
                const txGroups = [...groups.values()].reverse();
                const headSx = {
                  backgroundColor: "#f8fafc",
                  color: "#64748b",
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: 0.2,
                  textTransform: "capitalize",
                  textAlign: "center",
                  borderBottom: "1px solid #e5e7eb",
                  borderRight: "1px solid #f1f5f9",
                  py: 1.1,
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                };
                const cellSx = {
                  borderBottom: "1px solid #f1f5f9",
                  borderRight: "1px solid #f1f5f9",
                  py: 1.25,
                  px: 1.5,
                  fontSize: 13,
                  color: "#334155",
                  whiteSpace: "nowrap",
                };
                const amountSx = {
                  ...cellSx,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 600,
                  color: "#0f172a",
                };
                const balanceSx = {
                  ...amountSx,
                  textAlign: "center",
                };
                const iconBtnSx = {
                  color: "#64748b",
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#fff",
                  mx: 0.15,
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    backgroundColor: "var(--primary-light)",
                    color: "var(--primary-color)",
                    borderColor: "var(--primary-color)",
                  },
                };

                const labelTone = (label) => {
                  if (label === "Sales")
                    return { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" };
                  if (label === "Cash / Online Payment")
                    return { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" };
                  if (label === "Discount")
                    return { bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" };
                  if (label === "Tray Payment")
                    return { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" };
                  if (label === "Loading Charges")
                    return { bg: "#f8fafc", color: "#475569", border: "#e2e8f0" };
                  if (label === "Damage Refund")
                    return { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" };
                  return { bg: "#f8fafc", color: "#334155", border: "#e2e8f0" };
                };

                const rowBgForLabel = (label) => {
                  if (label === "Sales") return "#f0f7ff";
                  if (label === "Cash / Online Payment") return "#f0fdf6";
                  if (label === "Tray Payment") return "#fff8f1";
                  return "transparent";
                };

                const fmt = (v) =>
                  v === "" || v == null
                    ? "—"
                    : Math.round(Number(v) || 0).toLocaleString("en-IN");

                return (
                  <Table stickyHeader size="small" sx={{ minWidth: 960 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...headSx, textAlign: "left", pl: 2 }}>
                          Date
                        </TableCell>
                        <TableCell sx={{ ...headSx, textAlign: "left" }}>
                          Description
                        </TableCell>
                        <TableCell sx={{ ...headSx, textAlign: "left" }}>
                          Item
                        </TableCell>
                        <TableCell sx={headSx}>Rate</TableCell>
                        <TableCell sx={headSx}>Qty (Box)</TableCell>
                        <TableCell sx={headSx}>Amount</TableCell>
                        <TableCell sx={headSx}>Total</TableCell>
                        <TableCell sx={headSx}>Balance</TableCell>
                        <TableCell sx={headSx}>Transaction By</TableCell>
                        <TableCell sx={headSx}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {txGroups.map((group, gIdx) => {
                        const tx = group.tx;
                        const labelOf = (r) => {
                          if (/^TRY PAYMENT/.test(r.description)) return "Tray Payment";
                          if (r.description === "Payment Received")
                            return "Cash / Online Payment";
                          return r.description;
                        };
                        // Merge consecutive lines sharing one description label
                        const lines = [];
                        let head = null;
                        group.rows.forEach((r) => {
                          const label = labelOf(r);
                          // Sales-type groups total the amount; payment-type groups total the payment
                          const value = /^TRY PAYMENT/.test(r.description)
                            ? r.excluded
                              ? 0
                              : r.displayAmount
                            : r.payment
                            ? r.payment
                            : r.amount;
                          const net = r.amount - r.payment;
                          if (head && head.label === label) {
                            head.span += 1;
                            head.total += value;
                            head.net += net;
                            head.excluded = head.excluded && !!r.excluded;
                            lines.push({ row: r, label, span: 0 });
                          } else {
                            head = {
                              row: r,
                              label,
                              span: 1,
                              total: value,
                              net,
                              excluded: !!r.excluded,
                              due: 0,
                            };
                            lines.push(head);
                          }
                        });
                        // Due is counted from the last section upwards
                        const first = group.rows[0];
                        let runningDue = first ? first.balance - (first.amount - first.payment) : 0;
                        [...lines]
                          .reverse()
                          .filter((l) => l.span > 0)
                          .forEach((l) => {
                            runningDue += l.net;
                            l.due = runningDue;
                          });
                        return lines.map((line, i) => {
                          const sectionBg = rowBgForLabel(line.label);
                          return (
                          <TableRow
                            key={`${tx?._id || "tx"}-${i}`}
                            hover
                            sx={{
                              backgroundColor: sectionBg || "#ffffff",
                              ...(i === 0 &&
                                gIdx > 0 && {
                                  "& > td": {
                                    borderTop: "1px solid #e5e7eb",
                                  },
                                }),
                              "&.MuiTableRow-hover:hover": {
                                backgroundColor: sectionBg
                                  ? sectionBg
                                  : "#f8fafc",
                                filter: sectionBg ? "brightness(0.98)" : "none",
                              },
                            }}
                          >
                            {i === 0 && (
                              <TableCell
                                rowSpan={lines.length}
                                sx={{
                                  ...cellSx,
                                  verticalAlign: "middle",
                                  textAlign: "left",
                                  borderLeft: "3px solid var(--primary-color)",
                                  backgroundColor: "#fff",
                                  minWidth: 88,
                                  pl: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    px: 1.25,
                                    py: 0.75,
                                    borderRadius: 1.5,
                                    backgroundColor: "#fff",
                                    border: "1px solid #e2e8f0",
                                    minWidth: 64,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 20,
                                      fontWeight: 800,
                                      lineHeight: 1,
                                      color: "#0f172a",
                                    }}
                                  >
                                    {dayjs(tx.dateOfTransaction).format("DD")}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: 10.5,
                                      fontWeight: 700,
                                      letterSpacing: 0.4,
                                      textTransform: "uppercase",
                                      color: "#64748b",
                                      mt: 0.35,
                                    }}
                                  >
                                    {dayjs(tx.dateOfTransaction).format("MMM YY")}
                                  </Typography>
                                </Box>
                              </TableCell>
                            )}
                            {line.span > 0 && (() => {
                              const tone = labelTone(line.label);
                              return (
                              <TableCell
                                rowSpan={line.span}
                                colSpan={
                                  line.label === "Loading Charges"
                                    ? 2
                                    : line.label === "Discount" ||
                                      line.label === "Cash / Online Payment"
                                    ? 5
                                    : 1
                                }
                                sx={{
                                  ...cellSx,
                                  verticalAlign: "middle",
                                  backgroundColor: "transparent",
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-block",
                                    px: 1.1,
                                    py: 0.35,
                                    borderRadius: 1.25,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    backgroundColor: tone.bg,
                                    color: tone.color,
                                    border: `1px solid ${tone.border}`,
                                  }}
                                >
                                  {line.label}
                                </Box>
                              </TableCell>
                              );
                            })()}
                            {line.label !== "Loading Charges" &&
                              line.label !== "Discount" &&
                              line.label !== "Cash / Online Payment" && (
                                <TableCell
                                  sx={{
                                    ...cellSx,
                                    fontWeight: 500,
                                    color: "#1e293b",
                                  }}
                                >
                                  {/^TRY PAYMENT/.test(line.row.description)
                                    ? line.row.description.replace(
                                        /^TRY PAYMENT\s*/,
                                        ""
                                      )
                                    : line.row.item || "—"}
                                </TableCell>
                              )}
                            {line.label !== "Discount" &&
                              line.label !== "Cash / Online Payment" && (
                                <>
                                  <TableCell sx={amountSx}>
                                    {fmt(line.row.displayRate)}
                                  </TableCell>
                                  <TableCell sx={amountSx}>
                                    {fmt(line.row.quantity)}
                                  </TableCell>
                                  <TableCell sx={amountSx}>
                                    {fmt(line.row.displayAmount)}
                                  </TableCell>
                                </>
                              )}
                            {line.span > 0 && (
                              <TableCell
                                rowSpan={line.span}
                                sx={{
                                  ...amountSx,
                                  verticalAlign: "middle",
                                  fontWeight: 700,
                                  color: "#0f172a",
                                }}
                              >
                                {line.excluded ? (
                                  <Chip
                                    size="small"
                                    label="No Cost"
                                    sx={{
                                      height: 22,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      backgroundColor: "#f1f5f9",
                                      color: "#64748b",
                                    }}
                                  />
                                ) : (
                                  fmt(line.total)
                                )}
                              </TableCell>
                            )}
                            {line.span > 0 && (
                              <TableCell
                                rowSpan={line.span}
                                sx={{ ...balanceSx, verticalAlign: "middle" }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: 14,
                                    fontVariantNumeric: "tabular-nums",
                                    color: "var(--primary-color)",
                                  }}
                                >
                                  {fmt(line.due)}
                                </Typography>
                              </TableCell>
                            )}
                            {i === 0 && (
                              <TableCell
                                rowSpan={lines.length}
                                sx={{
                                  ...cellSx,
                                  verticalAlign: "middle",
                                  textAlign: "left",
                                  minWidth: 120,
                                  backgroundColor: "#fff",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#0f172a",
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {val(tx.transactionBy?.fullName)}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    color: "#94a3b8",
                                    mt: 0.35,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {new Date(tx?.createdAt).toLocaleDateString(
                                    "en-IN",
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      timeZone: "Asia/Kolkata",
                                    }
                                  )}
                                  {", "}
                                  {new Date(tx?.createdAt).toLocaleTimeString(
                                    "en-IN",
                                    {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                      timeZone: "Asia/Kolkata",
                                    }
                                  )}
                                </Typography>
                              </TableCell>
                            )}
                            {i === 0 && (
                              <>
                                <TableCell
                                  rowSpan={lines.length}
                                  sx={{
                                    ...cellSx,
                                    verticalAlign: "middle",
                                    textAlign: "center",
                                    backgroundColor: "#fff",
                                  }}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={0.35}
                                  >
                                    {user?.roleId !== 3 && (
                                      <Tooltip title="Edit Transaction" arrow placement="top">
                                        <IconButton
                                          color="primary"
                                          size="small"
                                          sx={iconBtnSx}
                                          onClick={() =>
                                            openEditTransactionPopup(tx)
                                          }
                                          aria-label="Edit transaction"
                                        >
                                          <FaEdit size={13} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Print Receipt" arrow placement="top">
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        sx={iconBtnSx}
                                        onClick={() => printTransaction(tx)}
                                        aria-label="Print receipt"
                                      >
                                        <FaPrint size={13} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Share on WhatsApp" arrow placement="top">
                                      <IconButton
                                        size="small"
                                        sx={{
                                          ...iconBtnSx,
                                          "&:hover": {
                                            backgroundColor: "#ecfdf5",
                                            color: "#25D366",
                                            borderColor: "#86efac",
                                          },
                                        }}
                                        onClick={() =>
                                          shareTransactionToWhatsapp(tx)
                                        }
                                        aria-label="Share on WhatsApp"
                                      >
                                        <FaWhatsapp size={14} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                          );
                        });
                      })}
                    </TableBody>
                  </Table>
                );
              })()}
            </TableContainer>
            <TablePagination
              component="div"
              count={transactionList.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                flexShrink: 0,
                borderTop: "1px solid #e5e7eb",
                backgroundColor: "#fff",
                ".MuiTablePagination-toolbar": { minHeight: 48 },
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                  {
                    fontSize: 13,
                    color: "#64748b",
                    textTransform: "capitalize",
                  },
              }}
            />
          </Box>
        )}

        {/* Transaction Dialog */}
        <Dialog
          open={open}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              overflow: "hidden",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <DialogTitle
            variant="h6"
            fontWeight={700}
            sx={{
              flexShrink: 0,
              background: "var(--primary-gradient, var(--primary-color))",
              color: "var(--primary-contrast, #fff)",
              py: 1.75,
            }}
          >
            {editTransaction ? "Edit Transaction" : "Add Transaction"}
          </DialogTitle>

          <form
            onSubmit={handleSubmit(addTransaction)}
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <DialogContent
              dividers
              sx={{
                pt: 2.5,
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: "#64748b",
                }}
              >
                Include in this transaction
              </Typography>
              <Box
                display="flex"
                gap={1}
                flexWrap="wrap"
                sx={{
                  p: 1.25,
                  mb: 1,
                  borderRadius: 2,
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e5e7eb",
                }}
              >
                <FormControlLabel
                  sx={{
                    m: 0,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1.5,
                    backgroundColor: "#fff",
                    border: "1px solid #e2e8f0",
                  }}
                  control={
                    <Controller
                      name="stockGiven"
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} checked={field.value} size="small" />
                      )}
                    />
                  }
                  label={<Typography fontSize={13} fontWeight={600}>Sales</Typography>}
                />
                <FormControlLabel
                  sx={{
                    m: 0,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1.5,
                    backgroundColor: "#fff",
                    border: "1px solid #e2e8f0",
                  }}
                  control={
                    <Controller
                      name="paymentDone"
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} checked={field.value} size="small" />
                      )}
                    />
                  }
                  label={<Typography fontSize={13} fontWeight={600}>Payment Received</Typography>}
                />
                <FormControlLabel
                  sx={{
                    m: 0,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1.5,
                    backgroundColor: "#fff",
                    border: "1px solid #e2e8f0",
                  }}
                  control={
                    <Controller
                      name="traysReturned"
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} checked={field.value} size="small" />
                      )}
                    />
                  }
                  label={<Typography fontSize={13} fontWeight={600}>Tray Payment</Typography>}
                />
                <FormControlLabel
                  sx={{
                    m: 0,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1.5,
                    backgroundColor: "#fff",
                    border: "1px solid #e2e8f0",
                  }}
                  control={
                    <Controller
                      name="discountGiven"
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} checked={field.value} size="small" />
                      )}
                    />
                  }
                  label={<Typography fontSize={13} fontWeight={600}>Add Discount</Typography>}
                />
              </Box>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-in">
                <Controller
                  name="dateOfTransaction"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      sx={{ mt: 3 }}
                      label="Date of Transaction"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(v) => field.onChange(v ? v.toDate() : null)}
                      maxDate={dayjs()}
                      format="MMM D, YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: false,
                          error: !!fieldState.error,
                          helperText: fieldState.error?.message,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>

              {stockGiven && (
                <>
                  <Divider sx={{ my: 2.5 }} />

                  <Typography
                    fontWeight={700}
                    sx={{
                      fontSize: 14,
                      color: "#0f172a",
                      mb: 0.5,
                      pb: 0.75,
                      borderBottom: "2px solid var(--primary-light)",
                    }}
                  >
                    Sales
                  </Typography>

                  {salesFields.map((row, index) => {
                    const qty = watch(`sales.${index}.numberOfItems`);
                    const cost = watch(`sales.${index}.costPerItem`);

                    return (
                      <Box
                        key={row.id}
                        display="flex"
                        gap={2}
                        mt={2}
                        alignItems={"center"}
                      >
                        {/* <TextField
                          label="Item Name"
                          sx={{ width: "45%", flex: 1 }}
                          {...register(`sales.${index}.itemName`)}
                          error={!!errors?.sales?.[index]?.itemName}
                          helperText={errors?.sales?.[index]?.itemName?.message}
                        /> */}
                        <FormControl
                          sx={{ flex: 1 }}
                          error={!!errors?.sales?.[index]?.itemName}
                        >
                          <InputLabel>Item</InputLabel>

                          <Controller
                            name={`sales.${index}.itemName`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Item"
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value)}
                              >
                                {items.map((item) => (
                                  <MenuItem
                                    key={item._id}
                                    value={item.itemName}
                                  >
                                    {item.itemName}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />

                          <FormHelperText>
                            {errors?.sales?.[index]?.itemName?.message}
                          </FormHelperText>
                        </FormControl>
                        <FormControl
                          sx={{ flex: 1 }}
                          error={!!errors?.sales?.[index]?.trayTypeId}
                        >
                          <InputLabel>Tray Type</InputLabel>

                          <Controller
                            name={`sales.${index}.trayTypeId`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Tray Type"
                                value={field.value ?? ""}
                                onChange={(e) =>
                                  field.onChange(Number(e.target.value))
                                }
                              >
                                {activeTrayTypes.map((tray) => (
                                  <MenuItem
                                    key={tray.trayTypeId || tray.id}
                                    value={tray.trayTypeId || tray.id}
                                  >
                                    {tray.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />

                          <FormHelperText>
                            {errors?.sales?.[index]?.trayTypeId?.message}
                          </FormHelperText>
                        </FormControl>

                        <TextField
                          sx={{ flex: 1 }}
                          label="Qty"
                          type="number"
                          {...register(`sales.${index}.numberOfItems`, {
                            setValueAs: (v) =>
                              v === "" ? undefined : Number(v),
                          })}
                          error={!!errors?.sales?.[index]?.numberOfItems}
                          helperText={
                            errors?.sales?.[index]?.numberOfItems?.message
                          }
                        />
                        <TextField
                          label="Cost"
                          type="number"
                          onWheel={(e) => e.target.blur()}
                          sx={{ flex: 1 }}
                          {...register(`sales.${index}.costPerItem`, {
                            setValueAs: (v) =>
                              v === "" ? undefined : Number(v),
                          })}
                          error={!!errors?.sales?.[index]?.costPerItem}
                          helperText={
                            errors?.sales?.[index]?.costPerItem?.message
                          }
                        />
                        <TextField
                          label="Amount"
                          readonly
                          sx={{ flex: 1 }}
                          value={qty && cost ? qty * cost : ""}
                        />

                        {salesFields.length > 1 && (
                          <Button color="error" onClick={() => remove(index)}>
                            <FaTrash />
                          </Button>
                        )}
                      </Box>
                    );
                  })}

                  <Button
                    sx={{ mt: 2 }}
                    variant="outlined"
                    onClick={() =>
                      append({
                        itemName: "",
                        numberOfItems: "",
                        costPerItem: "",
                      })
                    }
                  >
                    + Add More
                  </Button>

                  <Box display="flex" alignItems="center" mt={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...register("labourCharges.enabled")}
                          checked={Boolean(labourEnabled)}
                        />
                      }
                      label="Add Loading Charges"
                    />
                    {editTransaction && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...register("damageRefund.enabled")}
                            checked={Boolean(damageRefundEnabled)}
                          />
                        }
                        label="Add Damage Refund"
                      />
                    )}
                  </Box>

                  {labourEnabled && (
                    <>
                      <Typography
                        fontWeight={700}
                        sx={{
                          fontSize: 13,
                          color: "#334155",
                          mt: 1.5,
                          mb: 0.5,
                        }}
                      >
                        Loading Charges
                      </Typography>
                      <Box display="flex" gap={2} mt={2} alignItems="center">
                        <TextField
                          label="Qty"
                          type="number"
                          sx={{ flex: 1 }}
                          value={totalSalesQty || ""}
                          InputProps={{ readOnly: true }}
                        />
                        <TextField
                          label="Cost"
                          type="number"
                          onWheel={(e) => e.target.blur()}
                          sx={{ flex: 1 }}
                          {...register(`labourCharges.costPerItem`, {
                            setValueAs: (v) =>
                              v === "" ? undefined : Number(v),
                          })}
                          error={!!errors?.labourCharges?.costPerItem}
                          helperText={
                            errors?.labourCharges?.costPerItem?.message
                          }
                        />
                        <TextField
                          label="Total Amount"
                          sx={{ flex: 1 }}
                          value={
                            totalSalesQty && labourCost
                              ? totalSalesQty * Number(labourCost)
                              : ""
                          }
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                    </>
                  )}



                  {editTransaction && damageRefundEnabled && (
                    <>
                      <Typography
                        fontWeight={700}
                        sx={{
                          fontSize: 13,
                          color: "#334155",
                          mt: 1.5,
                          mb: 0.5,
                        }}
                      >
                        Damage Refund
                      </Typography>
                      {damageRefundForm.fields.map((field, index) => {
                        const currentRefund = damageRefunds[index] || {};
                        const selectedSaleIndex = Number.isFinite(
                          Number(currentRefund.selectedSaleIndex)
                        )
                          ? Number(currentRefund.selectedSaleIndex)
                          : undefined;
                        const selectedSale = Number.isFinite(selectedSaleIndex)
                          ? salesValues[selectedSaleIndex]
                          : null;
                        const selectedTrayType =
                          selectedSale?.trayTypeId && trayTypes.length
                            ? trayTypes.find(
                                (tray) =>
                                  tray.trayTypeId === selectedSale.trayTypeId ||
                                  tray.id === selectedSale.trayTypeId
                              )
                            : null;
                        const costPerItem = selectedSale
                          ? Math.max(
                              Number(selectedSale.costPerItem || 0) -
                                Number(selectedTrayType?.price || 0),
                              0
                            )
                          : "";
                        const maxQty = Number(selectedSale?.numberOfItems || 0);
                        const qty = Number(currentRefund.numberOfItems || 0);
                        const totalRefund =
                          costPerItem && qty ? qty * costPerItem : "";

                        return (
                          <Box
                            display="flex"
                            gap={2}
                            mt={2}
                            alignItems="center"
                            key={field.id}
                          >
                            <FormControl
                              sx={{ flex: 1 }}
                              error={
                                !!errors?.damageRefunds?.[index]
                                  ?.selectedSaleIndex
                              }
                            >
                              <InputLabel>Select Item</InputLabel>
                              <Controller
                                name={`damageRefunds.${index}.selectedSaleIndex`}
                                control={control}
                                render={({ field: selectField }) => (
                                  <Select
                                    {...selectField}
                                    value={selectField.value ?? ""}
                                    label="Select Item"
                                    onChange={(e) => {
                                      selectField.onChange(
                                        Number(e.target.value)
                                      );
                                      setValue(
                                        `damageRefunds.${index}.numberOfItems`,
                                        undefined,
                                        {
                                          shouldValidate: true,
                                          shouldDirty: true,
                                        }
                                      );
                                    }}
                                  >
                                    {salesValues.map((sale, idx) => (
                                      <MenuItem
                                        key={`${sale.itemName}-${idx}`}
                                        value={idx}
                                      >
                                        {sale.itemName || `Item ${idx + 1}`}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                )}
                              />
                              <FormHelperText>
                                {
                                  errors?.damageRefunds?.[index]
                                    ?.selectedSaleIndex?.message
                                }
                              </FormHelperText>
                            </FormControl>

                            <TextField
                              sx={{ flex: 1 }}
                              label="Qty"
                              type="number"
                              inputProps={{
                                min: 1,
                                max: maxQty,
                              }}
                              {...register(
                                `damageRefunds.${index}.numberOfItems`,
                                {
                                  setValueAs: (v) =>
                                    v === "" ? undefined : Number(v),
                                  validate: (value) => {
                                    if (!value) return true;
                                    return (
                                      value <= maxQty ||
                                      `Maximum allowed is ${maxQty}`
                                    );
                                  },
                                }
                              )}
                              error={
                                !!errors?.damageRefunds?.[index]?.numberOfItems
                              }
                              helperText={
                                errors?.damageRefunds?.[index]?.numberOfItems
                                  ?.message
                              }
                            />

                            <TextField
                              label="Cost"
                              type="number"
                              sx={{ flex: 1 }}
                              value={costPerItem}
                              InputProps={{ readOnly: true }}
                            />

                            <TextField
                              label="Total Refund"
                              sx={{ flex: 1 }}
                              value={totalRefund}
                              InputProps={{ readOnly: true }}
                            />

                            {damageRefundForm.fields.length > 1 && (
                              <Button
                                color="error"
                                onClick={() => damageRefundForm.remove(index)}
                              >
                                <FaTrash />
                              </Button>
                            )}
                          </Box>
                        );
                      })}

                      <Button
                        sx={{ mt: 2 }}
                        variant="outlined"
                        onClick={() =>
                          damageRefundForm.append({
                            selectedSaleIndex: undefined,
                            numberOfItems: undefined,
                            costPerItem: undefined,
                            total: undefined,
                          })
                        }
                      >
                        + Add More
                      </Button>
                    </>
                  )}
                </>
              )}

              {paymentDone && (
                <>
                  <Divider sx={{ my: 2.5 }} />

                  <Typography
                    fontWeight={700}
                    sx={{
                      fontSize: 14,
                      color: "#0f172a",
                      mb: 0.5,
                      pb: 0.75,
                      borderBottom: "2px solid var(--primary-light)",
                    }}
                  >
                    Payment
                  </Typography>

                  <Box display="flex" gap={2} mt={2}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      {...register(`paymentAmount`, {
                        setValueAs: (v) => (v === "" ? undefined : Number(v)),
                      })}
                      error={!!errors?.paymentAmount}
                      helperText={errors?.paymentAmount?.message}
                    />
                  </Box>
                </>
              )}

              {discountGiven && (
                <>
                  <Divider sx={{ my: 2.5 }} />

                  <Typography
                    fontWeight={700}
                    sx={{
                      fontSize: 14,
                      color: "#0f172a",
                      mb: 0.5,
                      pb: 0.75,
                      borderBottom: "2px solid var(--primary-light)",
                    }}
                  >
                    Discount
                  </Typography>

                  <Box display="flex" gap={2} mt={2}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      {...register(`discount`, {
                        setValueAs: (v) =>
                          v === "" ? undefined : Number(v),
                      })}
                      error={!!errors?.discount}
                      helperText={errors?.discount?.message}
                    />
                  </Box>
                </>
              )}

              {traysReturned && (
                <>
                  <Divider sx={{ my: 2.5 }} />

                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      pb: 0.75,
                      borderBottom: "2px solid var(--primary-light)",
                    }}
                  >
                    <Typography fontWeight={700} sx={{ fontSize: 14, color: "#0f172a" }}>
                      Tray Payment
                    </Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={excludePayment}
                          onChange={(e) => setExcludePayment(e.target.checked)}
                        />
                      }
                      label="Exclude Payment"
                    />
                  </Box>

                  {trayForm.fields.map((row, index) => {
                    const qty = watch(`trays.${index}.numberOfTrays`) ?? 0;
                    const trayId = watch(`trays.${index}.trayTypeId`);
                    const isExcluded = excludePayment;
                    const selectedTrayType =
                      trayId && trayTypes.length
                        ? trayTypes.find(
                            (tray) =>
                              tray.trayTypeId === trayId || tray.id === trayId
                          )
                        : null;
                    const costOfSelectedTray = selectedTrayType?.price || 0;
                    const availableBalance = getTrayBalanceCount(trayId);

                    const originalQty =
                      editTransaction &&
                      selectedTransaction?.trays?.[index]?.numberOfTrays
                        ? Number(selectedTransaction.trays[index].numberOfTrays)
                        : 0;

                    const maxQty = editTransaction
                      ? availableBalance + originalQty
                      : availableBalance;

                    const availableTrayTypes = (
                      customerDetails?.trayBalance || []
                    ).filter((tray) => {
                      const currentTrayId = tray.trayTypeId || tray.id;

                      if (currentTrayId === trayId) return true;
                      const hasValidCount =
                        tray.count !== undefined &&
                        tray.count !== null &&
                        Number(tray.count) > 0;

                      const isNotSelected =
                        !selectedTrayTypeIds.includes(currentTrayId);

                      return hasValidCount && isNotSelected;
                    });

                    return (
                      <div>
                        <Box
                          key={row.id}
                          display="flex"
                          gap={2}
                          mt={2}
                          alignItems="center"
                        >
                          <FormControl
                            sx={{ flex: 1 }}
                            error={!!errors?.trays?.[index]?.trayTypeId}
                          >
                            <InputLabel>Tray Type</InputLabel>

                            <Controller
                              name={`trays.${index}.trayTypeId`}
                              control={control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  label="Tray Type"
                                  value={field.value ?? ""}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                >
                                  {availableTrayTypes.map((tray) => (
                                    <MenuItem key={tray.id} value={tray.id}>
                                      {tray.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />

                            <FormHelperText>
                              {errors?.trays?.[index]?.trayTypeId?.message}
                            </FormHelperText>
                          </FormControl>

                          <TextField
                            sx={{ flex: 1 }}
                            label="Qty"
                            type="number"
                            inputProps={{
                              min: 1,
                              max: maxQty,
                            }}
                            {...register(`trays.${index}.numberOfTrays`, {
                              setValueAs: (v) =>
                                v === "" ? undefined : Number(v),
                              validate: (value) =>
                                value <= maxQty ||
                                `Maximum allowed is ${maxQty}`,
                            })}
                            error={!!errors?.trays?.[index]?.numberOfTrays}
                            helperText={
                              errors?.trays?.[index]?.numberOfTrays?.message
                            }
                          />
                          {/* <TextField
                          label="Cost"
                          type="number"
                          {...register(`trays.${index}.costPerTray`, {
                            setValueAs: (v) =>
                              v === "" ? undefined : Number(v),
                          })}
                          error={!!errors?.trays?.[index]?.costPerTray}
                          helperText={
                            errors?.trays?.[index]?.costPerTray?.message
                          }
                        /> */}

                          {!isExcluded && (
                            <TextField
                              label="Amount"
                              slotProps={{ input: { readOnly: true } }}
                              value={qty * costOfSelectedTray}
                              sx={{ flex: 1 }}
                            />
                          )}

                          {trayForm.fields.length > 1 && (
                            <Button
                              color="error"
                              onClick={() => trayForm.remove(index)}
                            >
                              <FaTrash />
                            </Button>
                          )}
                        </Box>
                      </div>
                    );
                  })}

                  {selectedTrayTypeIds.length < filteredTrayTypes.length && (
                    <Button
                      sx={{ mt: 2 }}
                      variant="outlined"
                      onClick={() =>
                        trayForm.append({
                          numberOfTrays: "",
                          costPerTray: "",
                        })
                      }
                    >
                      + Add More
                    </Button>
                  )}
                </>
              )}
            </DialogContent>

            <DialogActions
              sx={{
                flexShrink: 0,
                px: 3,
                py: 2,
                borderTop: "1px solid #e5e7eb",
                backgroundColor: "#f8fafc",
              }}
            >
              <Button
                onClick={() => {
                  closeTransactionPopup();
                }}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  saving ||
                  (!stockGiven &&
                    !paymentDone &&
                    !discountGiven &&
                    !traysReturned)
                }
                sx={{ textTransform: "none", fontWeight: 700, px: 2.5 }}
              >
                {saving ? "Saving" : "Submit"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* ✅ Payment Dialog */}
        <Dialog
          open={openPayment}
          fullWidth
          maxWidth="sm"
          onClose={closePaymentPopup}
          PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
        >
          <DialogTitle
            sx={{
              background: "var(--primary-gradient, var(--primary-color))",
              color: "var(--primary-contrast, #fff)",
              fontWeight: 700,
            }}
          >
            Add Payment
          </DialogTitle>

          <form noValidate onSubmit={paymentForm.handleSubmit(updatePayment)}>
            <DialogContent>
              <Box display="grid" gap={2} mt={1}>
                <Box display="flex" gap={2}>
                  <TextField
                    label="Amount"
                    type="number"
                    fullWidth
                    required
                    {...paymentForm.register("amount")}
                    error={!!paymentForm.formState.errors.amount}
                    helperText={paymentForm.formState.errors.amount?.message}
                    InputProps={{
                      startAdornment: <span style={{ marginRight: 6 }}>₹</span>,
                    }}
                  />

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller
                      name="paymentDate"
                      control={paymentForm.control}
                      render={({ field, fieldState }) => (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Payment Date"
                            value={field.value ? dayjs(field.value) : null}
                            onChange={(newValue) =>
                              field.onChange(
                                newValue ? newValue.toDate() : null
                              )
                            }
                            maxDate={dayjs()}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                error: !!fieldState?.error,
                                helperText: fieldState?.error?.message,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </LocalizationProvider>
                </Box>

                <Box display="flex" gap={2}>
                  <TextField
                    select
                    label="Payment Method"
                    fullWidth
                    required
                    defaultValue=""
                    {...paymentForm.register("paymentMethod")}
                    error={!!paymentForm.formState.errors.paymentMethod}
                    helperText={
                      paymentForm.formState.errors.paymentMethod?.message
                    }
                  >
                    <MenuItem value="NEFT">NEFT</MenuItem>
                    <MenuItem value="UPI">UPI</MenuItem>
                    <MenuItem value="Net Banking">Net Banking</MenuItem>
                    <MenuItem value="Cash">Cash</MenuItem>
                  </TextField>

                  <TextField
                    label="Reference ID"
                    fullWidth
                    {...paymentForm.register("referenceId")}
                    error={!!paymentForm.formState.errors.referenceId}
                    helperText={
                      paymentForm.formState.errors.referenceId?.message
                    }
                    placeholder="Enter transaction or reference number"
                  />
                </Box>
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                px: 3,
                py: 2,
                borderTop: "1px solid #e5e7eb",
                backgroundColor: "#f8fafc",
              }}
            >
              <Button
                onClick={closePaymentPopup}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{ textTransform: "none", fontWeight: 700, px: 2.5 }}
              >
                Submit
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        <TrayBalanceModal
          open={openTrayModal}
          onClose={() => setOpenTrayModal(false)}
          trayBalance={customerDetails?.trayBalance}
        />
        <Dialog
          open={showReceipt && !!receiptTransaction}
          onClose={() => {
            setShowReceipt(false);
            setReceiptTransaction(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent sx={{ p: 0, height: "75vh" }}>
            {receiptTransaction && (
              <iframe
                title="Transaction Receipt"
                srcDoc={buildTransactionHTML(receiptTransaction)}
                style={{ width: "100%", height: "100%", border: 0 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowReceipt(false);
                setReceiptTransaction(null);
              }}
              variant="outlined"
            >
              Close
            </Button>
            <Button
              onClick={() => shareTransactionToWhatsapp(receiptTransaction)}
              variant="outlined"
              color="success"
            >
              Share WhatsApp
            </Button>
            <Button
              onClick={() => printTransaction(receiptTransaction)}
              variant="contained"
            >
              Print
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={openDeleteDialog}
        onClose={cancelDeleteCustomer}
        onConfirm={confirmDeleteCustomer}
        title="Delete Customer"
        message="Are you sure you want to delete this customer? This action cannot be undone and will remove all associated data."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  );
}

export default CustomerDetails;
